import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Typography,
  Grid,
  TextField,
  Box,
  Paper,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import { styled } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import api from "../../globals/axios";
import { getAuth } from "../../globals/authentication";
import { useAlert } from "../../components/Alert";

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  console.log(expand);

  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const PickupOrderExpenseTracker = () => {
  const [expandedCards, setExpandedCards] = useState({});
  const [pickupOrders, setPickupOrders] = useState([]);
  const [totalData, setTotalData] = useState({
    total_quantity_kg: 0,
    total_amount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation();
  const user = getAuth();
  const { showAlert } = useAlert();
  const initialExpenseForm = {
    expense_date: dayjs(),
    expense_by: user.user.app_user.id,
    farmer_payment: "",
    driver_charge: "",
    fuel_charge: "",
    labour_charge: "",
    transport_charge: "",
    miscallenous_charge: "",
  };
  const [expenseForm, setExpenseForm] = useState(initialExpenseForm);

  useEffect(() => {
    const fetchPickupOrders = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const formattedDate = expenseForm.expense_date.format("YYYY-MM-DD");
        const response = await api.get(
          `/biochar/get-daily-expense/?date=${formattedDate}`
        );

        const ordersData = response.data.data;
        setPickupOrders(ordersData.pick_up_orders);
        setTotalData({
          total_quantity_kg: ordersData.total_quantity_kg,
          total_amount: ordersData.total_amount,
        });
      } catch (error) {
        if (error.response && error.response.status === 400) {
          setErrorMessage(error.response.data.message);
        } else {
          console.error(error);
          setErrorMessage(t("An unexpected error occurred."));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPickupOrders();
  }, [expenseForm.expense_date]);

  const handleExpandClick = (id) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Handler for expense form input changes
  const handleExpenseInputChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  // Handler for submitting expense form
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formattedExpenseForm = {
        ...expenseForm,
        expense_date: expenseForm.expense_date.format("YYYY-MM-DD"),
      };
      const response = await api.post(
        `/biochar/get-daily-expense/`,
        formattedExpenseForm
      );
      showAlert("success", response.data.message);
      setExpenseForm(initialExpenseForm);
      // Optionally, refresh the pickup orders or reset the form
    } catch (error) {
      console.error(error);
      showAlert("error", error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            py: 2,
          }}
        >
          {/* Date picker and summary sections */}
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("Select Date")}
                value={expenseForm.expense_date}
                onChange={(newValue) => {
                  if (newValue) {
                    setExpenseForm((prev) => ({
                      ...prev,
                      expense_date: newValue,
                    }));
                  }
                }}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { maxWidth: "400px", width: "100%" },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* Existing pickup orders section */}
          {!errorMessage && (
            <>
              <Paper
                elevation={3}
                sx={{
                  mt: 2,
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">{t("Total Quantity")}</Typography>
                <Typography variant="h6">
                  {totalData.total_quantity_kg} kg
                </Typography>
              </Paper>

              <Paper
                elevation={3}
                sx={{
                  mt: 1,
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">{t("Total Amount")}</Typography>
                <Typography variant="h6">
                  ₹{totalData.total_amount.toFixed(2)}
                </Typography>
              </Paper>
            </>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Rest of the existing component remains the same */}
          {!errorMessage && (
            <Grid container spacing={2}>
              {pickupOrders.map((order) => (
                <Grid item xs={12} key={order.id}>
                  <Card>
                    <Grid item xs={12} key={order.id}>
                      <Card>
                        <CardHeader
                          title={`${t("Order")}: ${order.id} - ${
                            order.plant_name
                          }`}
                          subheader={`${t("Transporter")}: ${
                            order.transporter_name
                          }`}
                          action={
                            <ExpandMore
                              expand={expandedCards[order.id]}
                              onClick={() => handleExpandClick(order.id)}
                              aria-expanded={expandedCards[order.id]}
                              aria-label="show more"
                            >
                              <ExpandMoreIcon />
                            </ExpandMore>
                          }
                        />
                        <Collapse
                          in={expandedCards[order.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <CardContent>
                            <Typography variant="h6">
                              {t("Procurements")}
                            </Typography>
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      <strong>{t("Farmer Name")}</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>{t("Crop Name")}</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>{t("Quantity (kg)")}</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>{t("Paid Amount (₹)")}</strong>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {order.procurements.map((item, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{item.farmer_name}</TableCell>
                                      <TableCell>{item.crop_name}</TableCell>
                                      <TableCell>{item.quantity_kg}</TableCell>
                                      <TableCell>
                                        ₹{item.paid_amount.toFixed(2)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {t("Total Quantity")}: {order.total_quantity_kg}{" "}
                              kg
                            </Typography>
                            <Typography variant="body2">
                              {t("Total Paid Amount")}: ₹
                              {order.total_paid_amount.toFixed(2)}
                            </Typography>
                          </CardContent>
                        </Collapse>
                      </Card>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Expense Form */}
          {!errorMessage && (
            <Card sx={{ mt: 2 }}>
              <CardHeader title="Add Daily Expenses" />
              <CardContent>
                <form onSubmit={handleExpenseSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Farmer Payment"
                        name="farmer_payment"
                        type="number"
                        value={expenseForm.farmer_payment}
                        onChange={handleExpenseInputChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Driver Charge"
                        name="driver_charge"
                        type="number"
                        value={expenseForm.driver_charge}
                        onChange={handleExpenseInputChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Fuel Charge"
                        name="fuel_charge"
                        type="number"
                        value={expenseForm.fuel_charge}
                        onChange={handleExpenseInputChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Labour Charge"
                        name="labour_charge"
                        type="number"
                        value={expenseForm.labour_charge}
                        onChange={handleExpenseInputChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Transport Charge"
                        name="transport_charge"
                        type="number"
                        value={expenseForm.transport_charge}
                        onChange={handleExpenseInputChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Miscellaneous Charge"
                        name="miscallenous_charge"
                        type="number"
                        value={expenseForm.miscallenous_charge}
                        onChange={handleExpenseInputChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                      >
                        {t("Submit Expenses")}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          )}
        </Container>
      </LoaderContainer>
    </div>
  );
};

export default PickupOrderExpenseTracker;
